import { useCallback, useEffect, useState } from "react"

//PrimeReact
import { InputText } from "primereact/inputtext"
import { InputTextarea } from "primereact/inputtextarea"
import { InputNumber } from "primereact/inputnumber"
import { Calendar } from "primereact/calendar"
import { Checkbox } from "primereact/checkbox"
import { Dropdown } from "primereact/dropdown"
import { RadioButton } from "primereact/radiobutton"

//Assessments components
import elementTypes from "../Elements/elementTypes"
import QuestionInputMatrix from "./QuestionInputMatrix"
import { InputMask } from "primereact/inputmask"
import { isValidPhoneNumber } from "react-phone-number-input"

//Styles
import formInputStyles from "../../../../../styles/Form-Inputs.module.css"
import myStyles from "../../../../../styles/assessments/ManagementFormBuilder.module.css"
import formStyles from "../../../../../styles/Form-Inputs.module.css"

//Yup
import * as Yup from "yup"
import QuestionInputAddress from "./QuestionInputAddress"
import {
  getAssessmentTimeOnlyFromIso,
  getAssessmentTimeOnlyISO,
} from "../../helpers/assessmentDateHelper"
import QuestionInputCalendar from "./QuestionInputCalendar"

export default function QuestionInput({
  question,
  answers,
  onSetAnswer,
  disabled,
}) {
  let answer = answers?.find((answer) => answer.questionId === question.id)
  let value = getDefaultValue()

  const [isValidValue, setIsValidValue] = useState(true)

  if (answer !== undefined) {
    value = answer.value
  }

  const setValue = (value) => {
    onSetAnswer(value)
  }

  const validateValue = useCallback(async () => {
    if (value && value !== "") {
      if (question.type === elementTypes.emailElementId) {
        const schema = Yup.string().email()

        const isValid = await schema.isValid(value)
        setIsValidValue(isValid)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useEffect(() => {
    validateValue()
  }, [validateValue])

  function getDefaultValue() {
    let defaultValue = undefined

    switch (question.type.toLowerCase()) {
      case elementTypes.textElementId:
        defaultValue = "Test value"
        break
      case elementTypes.numberElementId:
        defaultValue = 1
        break
      case elementTypes.dateElementId:
        defaultValue = new Date()
        break
      case elementTypes.checkboxElementId:
        defaultValue = [question.options[0].id]
        break
      case elementTypes.Dropdown:
        defaultValue = question.options[0].id
        break
      case elementTypes.radioElementId:
        defaultValue = question.options[0].id
        break
      case elementTypes.matrixElementId:
      case elementTypes.matrixCheckboxElementId:
      default:
        break
    }

    return defaultValue
  }

  switch (question.type.toLowerCase()) {
    case elementTypes.textElementId:
      return (
        <InputText
          className={"block"}
          style={{ width: "100%" }}
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
          }}
        />
      )
    case elementTypes.paragraphElementId:
      return (
        <InputTextarea
          className={"block"}
          style={{ width: "100%" }}
          value={value}
          onChange={(e) => {
            setValue(e.target.value)
          }}
          rows={question.height}
          autoResize
        />
      )
    case elementTypes.numberElementId:
      return (
        <InputNumber
          value={value}
          onChange={(e) => {
            setValue(e.value)
          }}
          style={{ width: "100%" }}
          inputStyle={{ width: "100%" }}
          minFractionDigits={0}
          mode={"decimal"}
          min={question.minValue !== "" ? question.minValue : undefined}
          max={question.maxValue !== "" ? question.maxValue : undefined}
        />
      )
    case elementTypes.dateElementId:
      return (
        <QuestionInputCalendar
          value={value}
          question={question}
          onSetValue={setValue}
        />
      )
    case elementTypes.timeElementId:
      let minTime = undefined
      let maxTime = undefined
      let timeValue = undefined

      if (value) {
        timeValue = getAssessmentTimeOnlyFromIso(value)
      }

      if (question.minValue) {
        minTime = getAssessmentTimeOnlyFromIso(question.minValue)
      }

      if (question.maxValue) {
        maxTime = getAssessmentTimeOnlyFromIso(question.maxValue)
      }

      return (
        <Calendar
          value={timeValue}
          onChange={(e) => {
            if (minTime?.isValid && minTime.toJSDate() > e.value) {
              setValue(getAssessmentTimeOnlyISO(minTime.toJSDate()))
            } else if (maxTime?.isValid && maxTime.toJSDate() < e.value) {
              setValue(getAssessmentTimeOnlyISO(maxTime.toJSDate()))
            } else {
              setValue(getAssessmentTimeOnlyISO(e.value))
            }
          }}
          style={{ width: "100%" }}
          showTime={true}
          hourFormat={"12"}
          showIcon
          iconPos="left"
          icon={<span class="material-icons">schedule</span>}
          timeOnly={true}
          maxDate={maxTime?.isValid ? maxTime.toJSDate() : undefined}
          minDate={minTime?.isValid ? minTime.toJSDate() : undefined}
        />
      )
    case elementTypes.checkboxElementId:
      return (
        <div className="radioGroup">
          {question.options
            .filter((option) => !option.isDeleted)
            .map((option) => {
              return (
                <div key={option.id}>
                  <Checkbox
                    checked={value?.includes(option.id)}
                    onChange={(e) => {
                      if (e.checked) {
                        setValue([...value, option.id])
                      } else {
                        setValue(value.filter((entry) => entry !== option.id))
                      }
                    }}
                  />
                  <label style={{ marginLeft: "10px" }}>{option.text}</label>
                </div>
              )
            })}
        </div>
      )
    case elementTypes.dropdownElementId:
      return (
        <Dropdown
          value={value}
          onChange={(e) => {
            setValue(e.value)
          }}
          options={question.options.filter((option) => !option.isDeleted)}
          optionLabel={"text"}
          optionValue={"id"}
          style={{ width: "100%" }}
        />
      )

    case elementTypes.radioElementId:
      return (
        <div className="radioGroup">
          {question.options
            .filter((option) => !option.isDeleted)
            .map((option) => (
              <div className="radioContainer" key={option.id}>
                <RadioButton
                  checked={value === option.id}
                  onChange={(e) => {
                    setValue(option.id)
                  }}
                />
                <label className="radioLabel" style={{ paddingLeft: "8px" }}>
                  {option.text}
                </label>
              </div>
            ))}
        </div>
      )
    case elementTypes.phoneElementId:
      const isValid =
        (value ? value.length === 0 : true) || isValidPhoneNumber(value, "US")

      return (
        <>
          <InputMask
            className={!isValid ? "p-invalid block" : "block"}
            style={{ width: "100%" }}
            mask="(999) 999-9999"
            placeholder="(999) 999-9999"
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
            }}
          />
          {!isValid ? (
            <label className={formInputStyles.errFieldMsg}>
              Invalid telephone number
            </label>
          ) : null}
        </>
      )
    case elementTypes.descriptionElementId:
    case elementTypes.headingElementId:
      return null //Heading elements dont use an input
    case elementTypes.emailElementId:
      const elementProfile = elementTypes.findTypeProfile(
        elementTypes.emailElementId
      )

      return (
        <div>
          <div className={myStyles.elementInputContainer}>
            <div className="icon">
              <span className={`material-icons`}>{elementProfile.icon}</span>
            </div>
            <InputText
              className={!isValidValue ? "p-invalid block" : "block"}
              style={{ width: "100%" }}
              value={value}
              onChange={(e) => {
                setValue(e.target.value)
              }}
              placeholder="mail@example.com"
              keyfilter="email"
            />
          </div>

          {!isValidValue ? (
            <label className={formStyles.errFieldMsg}>
              {"Invalid e-mail address"}
            </label>
          ) : null}
        </div>
      )
    case elementTypes.addressElementId:
      return (
        <QuestionInputAddress
          question={question}
          value={value}
          onValueChange={setValue}
        />
      )
    case elementTypes.matrixElementId:
    case elementTypes.matrixCheckboxElementId:
      return (
        <QuestionInputMatrix
          question={question}
          readOnly={false}
          answers={value}
          onUpdateAnswers={setValue}
        />
      )

    default:
      return <>Unknown question input type</>
  }
}
